import React, { useState } from 'react';
import { confirmAlert } from 'react-confirm-alert';
import 'react-confirm-alert/src/react-confirm-alert.css';

const OAuthClientsView = ({
                              clients,
                              isLoading,
                              isCreating,
                              isRegenerating,
                              isDeleting,
                              onCreateClient,
                              onRegenerateCredentials,
                              onDeleteClient,
                              onRefresh
                          }) => {
    const [newClientName, setNewClientName] = useState('');
    const [copyFeedback, setCopyFeedback] = useState({ show: false, text: '', type: '' });

    const handleCreateClient = (e) => {
        e.preventDefault();
        if (!newClientName.trim()) return;

        onCreateClient(newClientName.trim());
        setNewClientName('');
    };

    const handleRegenerate = (clientId, clientName) => {
        confirmAlert({
            title: 'Regenerate Credentials',
            message: `Are you sure you want to regenerate credentials for client "${clientName}"? This will invalidate the current credentials.`,
            buttons: [
                {
                    label: 'Yes',
                    onClick: () => onRegenerateCredentials(clientId)
                },
                {
                    label: 'No',
                    onClick: () => {}
                }
            ]
        });
    };

    const handleDelete = (clientId, clientName) => {
        confirmAlert({
            title: 'Delete Client',
            message: `Are you sure you want to delete client "${clientName}"? This action cannot be undone.`,
            buttons: [
                {
                    label: 'Yes',
                    onClick: () => onDeleteClient(clientId)
                },
                {
                    label: 'No',
                    onClick: () => {}
                }
            ]
        });
    };

    const showCopyFeedback = (text, type = 'success') => {
        setCopyFeedback({ show: true, text, type });
        setTimeout(() => setCopyFeedback({ show: false, text: '', type: '' }), 2000);
    };

    const copyToClipboard = async (text) => {
        if (!text) {
            showCopyFeedback('Nothing to copy', 'error');
            return;
        }

        try {
            // Method 1: Modern clipboard API (works in secure contexts)
            if (navigator.clipboard && window.isSecureContext) {
                await navigator.clipboard.writeText(text);
                showCopyFeedback('Copied to clipboard!');
                return;
            }

            // Method 2: execCommand fallback (works in most browsers including Firefox HTTP)
            const textArea = document.createElement('textarea');
            textArea.value = text;

            // Style to be hidden but visible enough for copy to work
            textArea.style.position = 'fixed';
            textArea.style.left = '0';
            textArea.style.top = '0';
            textArea.style.opacity = '0';
            textArea.style.pointerEvents = 'none';
            textArea.style.zIndex = '-1';

            document.body.appendChild(textArea);
            textArea.focus();
            textArea.select();

            try {
                const successful = document.execCommand('copy');
                document.body.removeChild(textArea);

                if (successful) {
                    showCopyFeedback('Copied to clipboard!');
                } else {
                    throw new Error('execCommand failed');
                }
            } catch (execErr) {
                document.body.removeChild(textArea);
                throw execErr;
            }

        } catch (err) {
            console.error('Failed to copy:', err);

            // Method 3: Fallback - show text in prompt for manual copy
            try {
                const textArea = document.createElement('textarea');
                textArea.value = text;
                textArea.style.position = 'fixed';
                textArea.style.left = '0';
                textArea.style.top = '0';
                textArea.style.opacity = '0';
                document.body.appendChild(textArea);
                textArea.select();

                showCopyFeedback('Text selected - press Ctrl+C to copy');

                // Auto-remove after a delay
                setTimeout(() => {
                    document.body.removeChild(textArea);
                }, 1000);

            } catch (fallbackErr) {
                showCopyFeedback('Failed to copy to clipboard', 'error');
            }
        }
    };

    const formatSecret = (secret) => {
        if (!secret) return 'Not available';
        return `${secret.substring(0, 8)}...${secret.substring(secret.length - 4)}`;
    };

    return (
        <div className="oauth-clients-section">
            {/* Copy Feedback Toast */}
            {copyFeedback.show && (
                <div className={`copy-feedback ${copyFeedback.type}`}>
                    <i className={`fas ${copyFeedback.type === 'success' ? 'fa-check-circle' : 'fa-exclamation-triangle'}`}></i>
                    {copyFeedback.text}
                </div>
            )}

            <div className="section-header">
                <h1>OAuth Clients Management</h1>
                <div className="header-actions">
                    <button
                        onClick={onRefresh}
                        disabled={isLoading}
                        className="btn btn-secondary"
                    >
                        <i className="fas fa-sync-alt"></i> Refresh
                    </button>
                </div>
            </div>

            <div className="create-client-form">
                <h2>Create New Client</h2>
                <form onSubmit={handleCreateClient} className="client-form">
                    <div className="form-group">
                        <input
                            type="text"
                            value={newClientName}
                            onChange={(e) => setNewClientName(e.target.value)}
                            placeholder="Enter client name"
                            disabled={isCreating}
                            required
                            className="client-name-input"
                        />
                    </div>
                    <button
                        type="submit"
                        disabled={isCreating || !newClientName.trim()}
                        className="btn btn-primary create-client-btn"
                    >
                        {isCreating ? 'Creating...' : 'Create Client'}
                    </button>
                </form>
            </div>

            <div className="clients-list">
                <h2>Existing Clients</h2>
                {isLoading ? (
                    <div className="loading-container">
                        <div className="spinner"></div>
                        <p>Loading clients...</p>
                    </div>
                ) : clients.length === 0 ? (
                    <div className="empty-state">
                        <i className="fas fa-users"></i>
                        <p>No clients found. Create your first client above.</p>
                    </div>
                ) : (
                    <div className="clients-grid">
                        {clients.map((client) => (
                            <div key={client.id} className="client-card">
                                <div className="client-header">
                                    <h3>{client.clientName}</h3>
                                    <div className="client-actions">
                                        <button
                                            onClick={() => handleRegenerate(client.id, client.clientName)}
                                            disabled={isRegenerating}
                                            className="btn btn-warning btn-sm"
                                            title="Regenerate Credentials"
                                        >
                                            <i className="fas fa-key"></i> Regenerate
                                        </button>
                                        <button
                                            onClick={() => handleDelete(client.id, client.clientName)}
                                            disabled={isDeleting}
                                            className="btn btn-danger btn-sm"
                                            title="Delete Client"
                                        >
                                            <i className="fas fa-trash"></i> Delete
                                        </button>
                                    </div>
                                </div>

                                <div className="client-details">
                                    <div className="client-field">
                                        <label>Client ID:</label>
                                        <div className="field-value">
                                            <span className="client-id">{client.clientId || 'Not available'}</span>
                                            <button
                                                onClick={() => copyToClipboard(client.clientId)}
                                                className="btn btn-icon"
                                                title="Copy Client ID"
                                                disabled={!client.clientId}
                                            >
                                                <i className="fas fa-copy"></i>
                                            </button>
                                        </div>
                                    </div>

                                    <div className="client-field">
                                        <label>Client Secret:</label>
                                        <div className="field-value">
                                            <span className="client-secret">{formatSecret(client.clientSecret)}</span>
                                            <button
                                                onClick={() => copyToClipboard(client.clientSecret)}
                                                className="btn btn-icon"
                                                title="Copy Client Secret"
                                                disabled={!client.clientSecret}
                                            >
                                                <i className="fas fa-copy"></i>
                                            </button>
                                        </div>
                                    </div>

                                    <div className="client-field">
                                        <label>Created:</label>
                                        <span>{client.dateCreated ? new Date(client.dateCreated).toLocaleDateString() : 'Unknown'}</span>
                                    </div>
                                </div>
                            </div>
                        ))}
                    </div>
                )}
            </div>

            <style jsx>{`
        .oauth-clients-section {
          padding: 20px;
          background: #fff;
          border-radius: 8px;
          box-shadow: 0 2px 10px rgba(0, 0, 0, 0.05);
          position: relative;
        }
        
        .copy-feedback {
          position: fixed;
          top: 20px;
          right: 20px;
          padding: 12px 20px;
          border-radius: 6px;
          color: white;
          font-weight: 500;
          z-index: 1000;
          box-shadow: 0 4px 12px rgba(0, 0, 0, 0.15);
          animation: slideIn 0.3s ease;
          display: flex;
          align-items: center;
          gap: 8px;
          max-width: 300px;
        }
        
        .copy-feedback.success {
          background: #28a745;
        }
        
        .copy-feedback.error {
          background: #dc3545;
        }
        
        @keyframes slideIn {
          from {
            transform: translateX(100%);
            opacity: 0;
          }
          to {
            transform: translateX(0);
            opacity: 1;
          }
        }
        
        .section-header {
          display: flex;
          justify-content: space-between;
          align-items: center;
          margin-bottom: 30px;
          padding-bottom: 15px;
          border-bottom: 1px solid #e0e0e0;
        }
        
        .section-header h1 {
          color: #1a3e72;
          margin: 0;
          font-size: 1.8rem;
        }
        
        .create-client-form {
          background: #f8f9fa;
          padding: 20px;
          border-radius: 8px;
          margin-bottom: 30px;
        }
        
        .create-client-form h2 {
          margin-top: 0;
          margin-bottom: 15px;
          color: #1a3e72;
        }
        
        .client-form {
          display: flex;
          gap: 15px;
          align-items: flex-end;
        }
        
        .client-name-input {
          flex: 1;
          padding: 10px 12px;
          border: 1px solid #ced4da;
          border-radius: 4px;
          font-size: 1rem;
          min-width: 250px;
        }
        
        .create-client-btn {
          white-space: nowrap;
        }
        
        .clients-list h2 {
          color: #1a3e72;
          margin-bottom: 20px;
        }
        
        .clients-grid {
          display: grid;
          grid-template-columns: repeat(auto-fill, minmax(350px, 1fr));
          gap: 20px;
        }
        
        .client-card {
          background: white;
          border-radius: 8px;
          padding: 20px;
          box-shadow: 0 2px 8px rgba(0, 0, 0, 0.08);
          border: 1px solid #e9ecef;
          transition: all 0.2s ease;
        }
        
        .client-card:hover {
          box-shadow: 0 4px 12px rgba(0, 0, 0, 0.12);
          transform: translateY(-2px);
        }
        
        .client-header {
          display: flex;
          justify-content: space-between;
          align-items: center;
          margin-bottom: 15px;
          padding-bottom: 15px;
          border-bottom: 1px solid #e9ecef;
        }
        
        .client-header h3 {
          margin: 0;
          color: #333;
          font-size: 1.1rem;
        }
        
        .client-actions {
          display: flex;
          gap: 8px;
        }
        
        .client-details {
          display: flex;
          flex-direction: column;
          gap: 12px;
        }
        
        .client-field {
          display: flex;
          justify-content: space-between;
          align-items: center;
        }
        
        .client-field label {
          font-weight: 600;
          color: #555;
          margin-right: 10px;
          min-width: 100px;
        }
        
        .field-value {
          display: flex;
          align-items: center;
          gap: 8px;
        }
        
        .client-id, .client-secret {
          font-family: monospace;
          font-size: 0.9rem;
          background: #f8f9fa;
          padding: 4px 8px;
          border-radius: 4px;
          border: 1px solid #e9ecef;
        }
        
        .btn-icon {
          padding: 6px;
          font-size: 12px;
          background-color: #f8f9fa;
          color: #6c757d;
          border: 1px solid #dee2e6;
          border-radius: 4px;
          cursor: pointer;
          transition: all 0.2s ease;
        }
        
        .btn-icon:hover:not(:disabled) {
          background-color: #e9ecef;
          transform: scale(1.05);
        }
        
        .btn-icon:disabled {
          opacity: 0.5;
          cursor: not-allowed;
          transform: none;
        }
        
        .empty-state {
          text-align: center;
          padding: 40px 20px;
          color: #6c757d;
        }
        
        .empty-state i {
          font-size: 48px;
          margin-bottom: 15px;
          display: block;
        }
        
        .loading-container {
          text-align: center;
          padding: 40px 20px;
        }
        
        .spinner {
          border: 3px solid #f3f3f3;
          border-top: 3px solid #3498db;
          border-radius: 50%;
          width: 40px;
          height: 40px;
          animation: spin 1s linear infinite;
          margin: 0 auto 15px;
        }
        
        @keyframes spin {
          0% { transform: rotate(0deg); }
          100% { transform: rotate(360deg); }
        }
        
        @media (max-width: 768px) {
          .client-form {
            flex-direction: column;
            align-items: stretch;
          }
          
          .client-name-input {
            min-width: auto;
          }
          
          .clients-grid {
            grid-template-columns: 1fr;
          }
          
          .copy-feedback {
            top: 10px;
            right: 10px;
            left: 10px;
            text-align: center;
          }
        }
      `}</style>
        </div>
    );
};

export default OAuthClientsView;